/* JD1 · CP-S2 V2.0 · Inline Predictive Search
   Native-friendly Shopify Ajax Predictive Search API.
   Works with snippets/search-inline.liquid replacement. V2.0 supports Retail OS native mobile search row from first paint. V1.9 kept one singleton mobile search row and prevented sticky duplicate rows. V1.8 kept the mobile search row intact and lets the native drawer visually cover it; it only closes predictive results.
*/
(() => {
  if (customElements.get('pf-inline-search')) return;

  class PfInlineSearch extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.form = this.querySelector('[data-pf-inline-search-form]');
      this.input = this.querySelector('[data-pf-inline-search-input]');
      this.results = this.querySelector('[data-pf-inline-search-results]');
      this.status = this.querySelector('[data-pf-inline-search-status]');
      this.clearButton = this.querySelector('[data-pf-inline-search-clear]');
      this.submitButton = this.querySelector('.pf-inline-search__submit');
      this.cluster = this.closest('.pf-search-cluster');
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.homeMarker = null;
      this.mobileRow = null;
      this.drawerTimer = null;
      this.drawerObserver = null;
      this.handleDrawerState = null;

      this.timer = null;
      this.abortController = null;
      this.lastTerm = '';
      this.opened = false;

      if (!this.form || !this.input || !this.results) return;

      this.handleMobilePlacement = this.placeForViewport.bind(this);
      this.mobileQuery.addEventListener?.('change', this.handleMobilePlacement);
      window.addEventListener('resize', this.debounce(() => this.placeForViewport(), 150));
      document.addEventListener('shopify:section:load', this.handleMobilePlacement);
      this.placeForViewport();
      this.toggleClear(this.input.value.trim().length > 0);
      this.setupDrawerGuard();

      this.onInput = this.debounce(this.handleInput.bind(this), 220);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onDocumentClick = this.handleDocumentClick.bind(this);
      this.onSubmit = this.handleSubmit.bind(this);
      this.form.addEventListener('submit', this.onSubmit);
      this.input.addEventListener('input', () => this.toggleClear(this.input.value.trim().length > 0));
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('focus', () => {
        if (this.results.innerHTML.trim() && this.input.value.trim().length >= 2) this.open();
      });
      this.input.addEventListener('keydown', this.onKeydown);

      this.clearButton?.addEventListener('click', () => {
        this.input.value = '';
        this.input.focus();
        this.close(true);
        this.toggleClear(false);
      });

      document.addEventListener('click', this.onDocumentClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('shopify:section:load', this.handleMobilePlacement);
      document.removeEventListener('toggle', this.handleDrawerState, true);
      document.removeEventListener('click', this.handleDrawerState, true);
      this.mobileQuery?.removeEventListener?.('change', this.handleMobilePlacement);
      this.form?.removeEventListener('submit', this.onSubmit);
      this.drawerObserver?.disconnect();
      this.abortController?.abort();
      window.clearTimeout(this.timer);
      window.clearTimeout(this.drawerTimer);
    }

    setupDrawerGuard() {
      this.handleDrawerState = () => this.scheduleDrawerState();
      document.addEventListener('toggle', this.handleDrawerState, true);
      document.addEventListener('click', this.handleDrawerState, true);

      this.drawerObserver = new MutationObserver(() => this.scheduleDrawerState());
      this.drawerObserver.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['open', 'class', 'aria-hidden', 'aria-expanded'],
      });

      this.updateDrawerState();
    }

    scheduleDrawerState() {
      window.clearTimeout(this.drawerTimer);
      this.drawerTimer = window.setTimeout(() => this.updateDrawerState(), 40);
    }

    updateDrawerState() {
      const open = this.mobileQuery.matches && this.isNativeDrawerOpen();

      // JD1 CP-S2 V1.7: keep the mobile search bar mounted and visible.
      // Opening the native drawer should only close predictive results, not hide or move the search row.
      if (open) this.close(true);
    }

    isNativeDrawerOpen() {
      const selectors = [
        'header-drawer details[open]',
        'menu-drawer details[open]',
        '.menu-drawer-container details[open]',
        'details[open] > .menu-drawer',
        'details[open] .menu-drawer',
        '.menu-drawer[open]',
        '.menu-drawer.is-open',
        '.menu-drawer.active',
        '.mobile-drawer[open]',
        '.mobile-drawer.is-open',
        '.mobile-drawer.active',
        '.drawer[open]',
        '.drawer.is-open',
        '.drawer.active',
        'dialog[open]',
      ];

      for (const selector of selectors) {
        const items = Array.from(document.querySelectorAll(selector));
        const found = items.some((item) => {
          if (!item) return false;
          if (this.contains(item) || this.cluster?.contains(item)) return false;
          const rect = item.getBoundingClientRect();
          return rect.width > 0 && rect.height > 0;
        });
        if (found) return true;
      }

      return false;
    }

    placeForViewport() {
      if (!this.cluster) return;

      const shouldMove = this.mobileQuery.matches;
      const header = this.findHeaderRoot();
      const nativeMobileRow = header?.hasAttribute('data-jd1-search-native-row');

      if (nativeMobileRow) {
        if (header) this.cleanMobileRows(header);

        if (shouldMove) {
          this.cluster.classList.add('pf-search-cluster--mobile-row');
        } else {
          this.cluster.classList.remove('pf-search-cluster--mobile-row');
          this.close();
        }

        this.scheduleDrawerState();
        return;
      }

      if (!this.homeMarker && this.cluster.parentNode) {
        this.homeMarker = document.createElement('span');
        this.homeMarker.hidden = true;
        this.homeMarker.setAttribute('data-pf-inline-search-home', '');
        this.cluster.before(this.homeMarker);
      }

      if (shouldMove) {
        if (!header) return;

        const row = this.getMobileRow(header);
        if (row && this.cluster.parentNode !== row) {
          row.append(this.cluster);
        }
        this.cluster.classList.add('pf-search-cluster--mobile-row');
      } else {
        if (this.homeMarker && this.homeMarker.parentNode && this.cluster.parentNode !== this.homeMarker.parentNode) {
          this.homeMarker.after(this.cluster);
        } else if (this.homeMarker && this.cluster.previousSibling !== this.homeMarker) {
          this.homeMarker.after(this.cluster);
        }
        this.cluster.classList.remove('pf-search-cluster--mobile-row');
        if (header) this.cleanMobileRows(header);
        this.close();
      }

      this.scheduleDrawerState();
    }

    findHeaderRoot() {
      return this.closest('#header-component, header, .header, .header-wrapper, sticky-header')
        || document.querySelector('#header-component, header, .header, .header-wrapper, sticky-header');
    }

    cleanMobileRows(header) {
      const rows = Array.from(header.querySelectorAll('.pf-mobile-search-row'));
      rows.forEach((row) => {
        if (!row.contains(this.cluster)) row.remove();
      });
    }

    getMobileRow(header) {
      const rows = Array.from(header.querySelectorAll('.pf-mobile-search-row'));
      let row = rows.find((candidate) => candidate.contains(this.cluster)) || rows[0] || null;

      rows.forEach((candidate) => {
        if (candidate !== row) candidate.remove();
      });

      if (row) return row;

      row = document.createElement('div');
      row.className = 'pf-mobile-search-row';
      row.setAttribute('data-pf-mobile-search-row', '');

      const topRow = header.querySelector(':scope > .header__row--top');
      const firstRow = header.querySelector(':scope > .header__row');
      const mainRow = topRow || firstRow;

      if (mainRow && mainRow.parentNode) {
        mainRow.after(row);
      } else {
        header.append(row);
      }

      return row;
    }

    debounce(fn, wait) {
      return (...args) => {
        window.clearTimeout(this.timer);
        this.timer = window.setTimeout(() => fn(...args), wait);
      };
    }

    handleDocumentClick(event) {
      if (this.contains(event.target)) return;
      this.close();
    }

    handleSubmit(event) {
      const term = this.input.value.trim();
      if (!term) {
        event.preventDefault();
        this.input.focus();
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        this.input.blur();
        return;
      }

      if (event.key === 'ArrowDown') {
        const firstLink = this.results.querySelector('a, button');
        if (firstLink && this.opened) {
          event.preventDefault();
          firstLink.focus();
        }
      }
    }

    async handleInput() {
      const term = this.input.value.trim();
      this.toggleClear(term.length > 0);

      if (term.length < 2) {
        this.close(true);
        return;
      }

      if (term === this.lastTerm && this.results.innerHTML.trim()) {
        this.open();
        return;
      }

      this.lastTerm = term;
      await this.fetchResults(term);
    }

    async fetchResults(term) {
      this.abortController?.abort();
      this.abortController = new AbortController();

      const endpoint = this.getEndpoint();
      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('resources[type]', this.dataset.resourcesType || 'query,product,collection');
      url.searchParams.set('resources[limit]', this.dataset.resourcesLimit || '4');
      url.searchParams.set('resources[limit_scope]', this.dataset.limitScope || 'each');
      url.searchParams.set('resources[options][unavailable_products]', this.dataset.unavailableProducts || 'last');
      url.searchParams.set(
        'resources[options][fields]',
        this.dataset.fields || 'variants.sku,title,product_type,variants.title,vendor'
      );

      const skuUrl = new URL(endpoint, window.location.origin);
      skuUrl.searchParams.set('q', term);
      skuUrl.searchParams.set('resources[type]', 'product');
      skuUrl.searchParams.set('resources[limit]', this.dataset.resourcesLimit || '4');
      skuUrl.searchParams.set('resources[limit_scope]', 'each');
      skuUrl.searchParams.set('resources[options][unavailable_products]', this.dataset.unavailableProducts || 'last');
      skuUrl.searchParams.set('resources[options][fields]', 'variants.sku');

      this.setStatus('Buscando…');
      this.classList.add('is-loading');

      try {
        const requestOptions = {
          signal: this.abortController.signal,
          headers: { Accept: 'application/json' },
        };
        const [response, skuResponse] = await Promise.all([
          fetch(url.toString(), requestOptions),
          fetch(skuUrl.toString(), requestOptions).catch(() => null),
        ]);

        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);

        const data = await response.json();
        if (skuResponse?.ok) {
          try {
            const skuData = await skuResponse.json();
            this.prioritizeSkuProducts(data, skuData);
          } catch (_error) {
            // The broad search remains functional if the SKU-only response is unavailable.
          }
        }
        const html = this.render(data, term);
        this.results.innerHTML = html;
        this.setStatus('');
        this.classList.remove('is-loading');
        this.open();
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.classList.remove('is-loading');
        this.results.innerHTML = this.renderEmpty(term);
        this.setStatus('');
        this.open();
      }
    }

    getEndpoint() {
      const explicit = this.dataset.predictiveJsonUrl;
      if (explicit) return explicit;

      const root = this.dataset.rootUrl || window.Shopify?.routes?.root || '/';
      return `${root.replace(/\/?$/, '/') }search/suggest.json`;
    }

    prioritizeSkuProducts(data, skuData) {
      const broadProducts = data?.resources?.results?.products;
      const skuProducts = skuData?.resources?.results?.products;
      if (!Array.isArray(broadProducts) || !Array.isArray(skuProducts) || !skuProducts.length) return;

      const seen = new Set();
      const productKey = (product) => String(product.id || product.handle || product.url || product.title || '');
      const skuFirst = skuProducts.map((product) => ({ ...product, _jd1SkuMatch: true }));
      const merged = [...skuFirst, ...broadProducts].filter((product) => {
        const key = productKey(product);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      data.resources.results.products = merged;
    }

    open() {
      if (this.mobileQuery.matches && this.isNativeDrawerOpen()) {
        this.updateDrawerState();
        return;
      }

      this.opened = true;
      this.classList.add('is-open');
      this.input.setAttribute('aria-expanded', 'true');
      this.results.hidden = false;
    }

    close(clear = false) {
      this.opened = false;
      this.classList.remove('is-open', 'is-loading');
      this.input.setAttribute('aria-expanded', 'false');
      this.results.hidden = true;
      this.setStatus('');
      if (clear) this.results.innerHTML = '';
    }

    toggleClear(show) {
      if (!this.clearButton) return;
      this.clearButton.hidden = !show;
    }

    setStatus(text) {
      if (this.status) this.status.textContent = text;
    }

    render(data, term) {
      const resources = data?.resources?.results || {};
      const queries = resources.queries || [];
      const products = resources.products || [];
      const collections = resources.collections || [];
      const pages = resources.pages || [];
      const articles = resources.articles || [];
      const hasResults = queries.length || products.length || collections.length || pages.length || articles.length;

      if (!hasResults) return this.renderEmpty(term);

      return `
        <div class="pf-inline-search__panel" role="listbox" aria-label="Resultados de búsqueda predictiva">
          ${this.renderQueries(queries)}
          ${this.renderProducts(products)}
          ${this.renderCollections(collections)}
          ${this.renderTextResources('Páginas', pages)}
          ${this.renderTextResources('Artículos', articles)}
          <a class="pf-inline-search__all" href="${this.searchUrl(term)}">
            Ver todos los resultados para “${this.escape(term)}”
          </a>
        </div>
      `;
    }

    renderQueries(items) {
      if (!items.length) return '';
      return `
        <section class="pf-inline-search__section pf-inline-search__section--queries">
          <h3 class="pf-inline-search__heading">Sugerencias</h3>
          <div class="pf-inline-search__chips">
            ${items.slice(0, 5).map((item) => {
              const text = item.text || item.styled_text || item.title || '';
              if (!text) return '';
              return `<a class="pf-inline-search__chip" role="option" href="${this.searchUrl(text)}">${this.escape(text)}</a>`;
            }).join('')}
          </div>
        </section>
      `;
    }

    renderProducts(items) {
      if (!items.length) return '';
      return `
        <section class="pf-inline-search__section">
          <h3 class="pf-inline-search__heading">Productos</h3>
          <div class="pf-inline-search__products">
            ${items.slice(0, 6).map((product) => this.renderProduct(product)).join('')}
          </div>
        </section>
      `;
    }

    renderProduct(product) {
      const title = product.title || '';
      const url = product.url || '#';
      const image = product.featured_image?.url || product.image || product.featured_image || '';
      const price = this.formatPrice(product.price || product.price_min || product.price_max || '');
      const badge = this.getProductBadge(product);

      return `
        <a class="pf-inline-search__product" role="option" href="${this.escapeAttr(url)}">
          <span class="pf-inline-search__product-media">
            ${image ? `<img src="${this.escapeAttr(image)}" alt="${this.escapeAttr(title)}" loading="lazy">` : ''}
          </span>
          <span class="pf-inline-search__product-body">
            ${product._jd1SkuMatch ? '<span class="pf-inline-search__product-sku-hit">Coincidencia SKU</span>' : ''}
            ${badge ? `<span class="pf-inline-search__product-vendor">${this.escape(badge)}</span>` : ''}
            <span class="pf-inline-search__product-title">${this.escape(title)}</span>
            ${price ? `<span class="pf-inline-search__product-price">${this.escape(price)}</span>` : ''}
          </span>
        </a>
      `;
    }

    getProductBadge(product) {
      const source = this.dataset.resultBadgeSource || 'product_type';
      const label = this.dataset.resultBadgeLabel || '';
      let value = '';

      if (source === 'none') {
        return '';
      }

      if (source === 'vendor') {
        value = product.vendor || '';
      } else if (source === 'product_type') {
        value = product.product_type || product.type || '';
      } else if (source === 'tag') {
        const tags = product.tags || product.tag || [];
        value = Array.isArray(tags) ? tags[0] : String(tags || '').split(',')[0];
      } else if (source === 'metafield') {
        /* Shopify Predictive Search JSON does not expose arbitrary metafields.
           This fallback keeps the UI stable while CP-S3 can move to section-rendered Liquid results. */
        value = product.metafield || product.metafield_value || '';
      }

      value = String(value || '').trim();
      if (!value) return '';
      return label ? `${label} ${value}` : value;
    }

    renderCollections(items) {
      if (!items.length) return '';
      return `
        <section class="pf-inline-search__section">
          <h3 class="pf-inline-search__heading">Colecciones</h3>
          <div class="pf-inline-search__links">
            ${items.slice(0, 4).map((collection) => `
              <a class="pf-inline-search__link" role="option" href="${this.escapeAttr(collection.url || '#')}">${this.escape(collection.title || '')}</a>
            `).join('')}
          </div>
        </section>
      `;
    }

    renderTextResources(label, items) {
      if (!items.length) return '';
      return `
        <section class="pf-inline-search__section">
          <h3 class="pf-inline-search__heading">${this.escape(label)}</h3>
          <div class="pf-inline-search__links">
            ${items.slice(0, 3).map((item) => `
              <a class="pf-inline-search__link" role="option" href="${this.escapeAttr(item.url || '#')}">${this.escape(item.title || '')}</a>
            `).join('')}
          </div>
        </section>
      `;
    }

    renderEmpty(term) {
      return `
        <div class="pf-inline-search__panel pf-inline-search__panel--empty">
          <p class="pf-inline-search__empty-title">No encontramos resultados rápidos.</p>
          <a class="pf-inline-search__all" href="${this.searchUrl(term)}">Buscar “${this.escape(term)}”</a>
        </div>
      `;
    }

    searchUrl(term) {
      const base = this.dataset.searchUrl || '/search';
      const url = new URL(base, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('options[prefix]', 'last');
      return `${url.pathname}${url.search}`;
    }

    formatPrice(value) {
      if (value == null || value === '') return '';

      const cleanString = (input) => String(input).replace(/<[^>]*>?/gm, '').trim();
      let amount;

      if (typeof value === 'number') {
        amount = value / 100;
      } else {
        const cleaned = cleanString(value).replace(/[^\d,.-]/g, '');
        if (!cleaned) return '';

        if (cleaned.includes(',') && cleaned.includes('.')) {
          const decimalSeparator = cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.') ? ',' : '.';
          const groupSeparator = decimalSeparator === ',' ? /\./g : /,/g;
          amount = Number(cleaned.replace(groupSeparator, '').replace(decimalSeparator, '.'));
        } else if (/^-?\d+[.,]\d{1,2}$/.test(cleaned)) {
          amount = Number(cleaned.replace(',', '.'));
        } else {
          amount = Number(cleaned.replace(/[.,]/g, ''));
        }
      }

      if (!Number.isFinite(amount)) return cleanString(value);
      const grouped = String(Math.round(amount)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return String.fromCharCode(36) + grouped;
    }

    escape(value) {
      return String(value).replace(/[&<>"]/g, (char) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
      }[char]));
    }

    escapeAttr(value) {
      return this.escape(value).replace(/'/g, '&#39;');
    }
  }

  customElements.define('pf-inline-search', PfInlineSearch);
})();